/**
 * Role-based home paths.
 *
 * When a demo user signs in, the login page bounces them straight to the
 * page most relevant to their role: a salesperson lands on their own
 * pipeline, an employee lands on self-service (with the attendance
 * clock-in button front-and-centre), an accountant on the accounting
 * dashboard, and everyone else on the ERP-wide home dashboard.
 *
 * Kept in one file so both the login page and any future guard/index-
 * route redirect share the same mapping — a mismatch there causes
 * hard-to-diagnose "why did I land back on /dashboard" bug reports.
 */

const ROLE_HOME: Record<string, string> = {
  Salesperson: '/sales/my-pipeline',
  Employee: '/self-service',
  Accountant: '/accounting',
  Manager: '/dashboard',
  Owner: '/dashboard',
};

/**
 * The path a user with these roles should land on after signing in.
 * Falls back to `/dashboard` when no known role matches — that's the
 * existing behaviour for admins and platform-admins.
 */
export function homePathForRoles(roles: readonly string[] | undefined | null): string {
  if (!roles || roles.length === 0) return '/dashboard';
  // If the user has multiple roles (e.g. Owner + Salesperson), the more
  // powerful one wins so an owner doesn't get quarantined to
  // /self-service. The order below is intentional.
  const priority = ['Owner', 'Manager', 'Accountant', 'Salesperson', 'Employee'];
  for (const p of priority) {
    if (roles.includes(p)) return ROLE_HOME[p] ?? '/dashboard';
  }
  return '/dashboard';
}
